/**
 * Leave & Movement Service
 * Full CRUD operations for resident leave records, hospital transfers,
 * bed holds, and status transitions with localStorage persistence.
 */

import LocalStorageService from '../storage/LocalStorageService';
import { logDataModification } from '../../utils/auditLogger';

const STORAGE_KEY = 'resident_leave_movements';

class LeaveMovementService {
  constructor() {
    if (!LocalStorageService.get(STORAGE_KEY)) {
      LocalStorageService.set(STORAGE_KEY, {});
    }
  }

  _getResidentLeave(residentId) {
    const all = LocalStorageService.get(STORAGE_KEY, {});
    return all[residentId] || null;
  }

  _persist(residentId, data) {
    const all = LocalStorageService.get(STORAGE_KEY, {});
    all[residentId] = { ...data, updatedAt: new Date().toISOString() };
    LocalStorageService.set(STORAGE_KEY, all);
    return all[residentId];
  }

  _generateId() {
    return `LM-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /**
   * Initialise persisted leave data for a resident from their mock/model data.
   * Called once when the Leave tab opens; merges base data with any prior edits.
   */
  initialise(residentId, baseLeaveData) {
    const existing = this._getResidentLeave(residentId);
    if (existing) return existing;

    const seed = {
      currentLeaveStatus: baseLeaveData?.currentLeaveStatus || 'In Facility',
      leaveHistory: (baseLeaveData?.leaveHistory || []).map((l, i) => ({
        ...l,
        id: l.id || `LH-seed-${i}`,
        createdAt: l.createdAt || new Date().toISOString(),
      })),
      hospitalTransfers: (baseLeaveData?.hospitalTransfers || []).map((t, i) => ({
        ...t,
        id: t.id || `HT-seed-${i}`,
        createdAt: t.createdAt || new Date().toISOString(),
      })),
      bedHold: baseLeaveData?.bedHold || { active: false, startDate: null, expectedReturn: null, reason: null },
      evacuationPriority: baseLeaveData?.evacuationPriority || null,
      createdAt: new Date().toISOString(),
    };

    return this._persist(residentId, seed);
  }

  getAll(residentId) {
    return this._getResidentLeave(residentId);
  }

  // ── Leave Records ────────────────────────────────────────────────────

  recordLeave(residentId, leaveData, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    const errors = this._validateLeave(leaveData, data);
    if (errors.length) return { success: false, errors };

    const record = {
      id: this._generateId(),
      type: leaveData.type,
      startDate: leaveData.startDate,
      endDate: leaveData.endDate || null,
      expectedReturnDate: leaveData.expectedReturnDate || null,
      destination: leaveData.destination || '',
      notes: leaveData.notes || '',
      contactWhileOnLeave: leaveData.contactWhileOnLeave || '',
      authorisedBy: leaveData.authorisedBy || userId,
      returnStatus: 'On Leave',
      createdAt: new Date().toISOString(),
      createdBy: userId,
    };

    data.leaveHistory.unshift(record);
    data.currentLeaveStatus = leaveData.type === 'Hospital Leave' ? 'On Leave (Hospital)' : 'On Leave (Social)';

    if (leaveData.activateBedHold) {
      data.bedHold = {
        active: true,
        startDate: leaveData.startDate,
        expectedReturn: leaveData.expectedReturnDate || null,
        reason: leaveData.type,
        leaveRecordId: record.id,
      };
    }

    this._persist(residentId, data);
    logDataModification('leave_record', record.id, 'CREATE', null, record, userId);
    return { success: true, data: record };
  }

  returnFromLeave(residentId, leaveRecordId, returnData, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    const idx = data.leaveHistory.findIndex((l) => l.id === leaveRecordId);
    if (idx === -1) return { success: false, error: 'Leave record not found' };

    const prev = { ...data.leaveHistory[idx] };
    data.leaveHistory[idx] = {
      ...data.leaveHistory[idx],
      endDate: returnData.returnDate || new Date().toISOString().split('T')[0],
      returnStatus: 'Returned',
      returnNotes: returnData.returnNotes || '',
      clinicalChangesOnReturn: returnData.clinicalChangesOnReturn || '',
      returnedBy: userId,
      updatedAt: new Date().toISOString(),
    };

    data.currentLeaveStatus = 'In Facility';

    if (data.bedHold?.active && data.bedHold.leaveRecordId === leaveRecordId) {
      data.bedHold = { active: false, startDate: null, expectedReturn: null, reason: null };
    }

    this._persist(residentId, data);
    logDataModification('leave_record', leaveRecordId, 'UPDATE', prev, data.leaveHistory[idx], userId);
    return { success: true, data: data.leaveHistory[idx] };
  }

  updateLeave(residentId, leaveRecordId, updates, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    const idx = data.leaveHistory.findIndex((l) => l.id === leaveRecordId);
    if (idx === -1) return { success: false, error: 'Leave record not found' };

    const prev = { ...data.leaveHistory[idx] };
    data.leaveHistory[idx] = {
      ...data.leaveHistory[idx],
      ...updates,
      updatedAt: new Date().toISOString(),
      updatedBy: userId,
    };

    this._persist(residentId, data);
    logDataModification('leave_record', leaveRecordId, 'UPDATE', prev, data.leaveHistory[idx], userId);
    return { success: true, data: data.leaveHistory[idx] };
  }

  deleteLeave(residentId, leaveRecordId, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    const record = data.leaveHistory.find((l) => l.id === leaveRecordId);
    if (!record) return { success: false, error: 'Leave record not found' };

    data.leaveHistory = data.leaveHistory.filter((l) => l.id !== leaveRecordId);

    if (record.returnStatus === 'On Leave') {
      data.currentLeaveStatus = 'In Facility';
    }
    if (data.bedHold?.leaveRecordId === leaveRecordId) {
      data.bedHold = { active: false, startDate: null, expectedReturn: null, reason: null };
    }

    this._persist(residentId, data);
    logDataModification('leave_record', leaveRecordId, 'DELETE', record, null, userId);
    return { success: true };
  }

  // ── Hospital Transfers ────────────────────────────────────────────────

  recordHospitalTransfer(residentId, transferData, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    const errors = this._validateTransfer(transferData);
    if (errors.length) return { success: false, errors };

    const record = {
      id: this._generateId(),
      dateOut: transferData.dateOut,
      hospital: transferData.hospital,
      reason: transferData.reason,
      referringDoctor: transferData.referringDoctor || '',
      ambulanceRequired: transferData.ambulanceRequired || false,
      familyNotified: transferData.familyNotified || false,
      familyNotifiedAt: transferData.familyNotifiedAt || null,
      transferSummaryId: transferData.transferSummaryId || null,
      dateReturned: transferData.dateReturned || null,
      clinicalChangesOnReturn: transferData.clinicalChangesOnReturn || '',
      status: transferData.dateReturned ? 'Returned' : 'In Hospital',
      createdAt: new Date().toISOString(),
      createdBy: userId,
    };

    data.hospitalTransfers.unshift(record);

    if (!transferData.dateReturned) {
      data.currentLeaveStatus = 'On Leave (Hospital)';
      data.bedHold = {
        active: true,
        startDate: transferData.dateOut,
        expectedReturn: transferData.expectedReturn || null,
        reason: 'Hospital Transfer',
        leaveRecordId: record.id,
      };
    }

    this._persist(residentId, data);
    logDataModification('hospital_transfer', record.id, 'CREATE', null, record, userId);
    return { success: true, data: record };
  }

  returnFromHospital(residentId, transferId, returnData, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    const idx = data.hospitalTransfers.findIndex((t) => t.id === transferId);
    if (idx === -1) return { success: false, error: 'Transfer record not found' };

    const prev = { ...data.hospitalTransfers[idx] };
    data.hospitalTransfers[idx] = {
      ...data.hospitalTransfers[idx],
      dateReturned: returnData.dateReturned || new Date().toISOString().split('T')[0],
      clinicalChangesOnReturn: returnData.clinicalChangesOnReturn || '',
      dischargeSummaryReceived: returnData.dischargeSummaryReceived || false,
      medicationChanges: returnData.medicationChanges || '',
      followUpRequired: returnData.followUpRequired || '',
      status: 'Returned',
      updatedAt: new Date().toISOString(),
      updatedBy: userId,
    };

    const hasOtherActiveTransfers = data.hospitalTransfers.some(
      (t, i) => i !== idx && t.status === 'In Hospital'
    );
    if (!hasOtherActiveTransfers) {
      data.currentLeaveStatus = 'In Facility';
      if (data.bedHold?.active && data.bedHold.leaveRecordId === transferId) {
        data.bedHold = { active: false, startDate: null, expectedReturn: null, reason: null };
      }
    }

    this._persist(residentId, data);
    logDataModification('hospital_transfer', transferId, 'UPDATE', prev, data.hospitalTransfers[idx], userId);
    return { success: true, data: data.hospitalTransfers[idx] };
  }

  updateTransfer(residentId, transferId, updates, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    const idx = data.hospitalTransfers.findIndex((t) => t.id === transferId);
    if (idx === -1) return { success: false, error: 'Transfer record not found' };

    const prev = { ...data.hospitalTransfers[idx] };
    data.hospitalTransfers[idx] = {
      ...data.hospitalTransfers[idx],
      ...updates,
      updatedAt: new Date().toISOString(),
      updatedBy: userId,
    };

    this._persist(residentId, data);
    logDataModification('hospital_transfer', transferId, 'UPDATE', prev, data.hospitalTransfers[idx], userId);
    return { success: true, data: data.hospitalTransfers[idx] };
  }

  // ── Bed Hold ──────────────────────────────────────────────────────────

  activateBedHold(residentId, bedHoldData, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    data.bedHold = {
      active: true,
      startDate: bedHoldData.startDate || new Date().toISOString().split('T')[0],
      expectedReturn: bedHoldData.expectedReturn || null,
      reason: bedHoldData.reason || 'Manual',
      leaveRecordId: bedHoldData.leaveRecordId || null,
    };

    this._persist(residentId, data);
    logDataModification('bed_hold', residentId, 'ACTIVATE', null, data.bedHold, userId);
    return { success: true, data: data.bedHold };
  }

  releaseBedHold(residentId, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    const prev = { ...data.bedHold };
    data.bedHold = { active: false, startDate: null, expectedReturn: null, reason: null };

    this._persist(residentId, data);
    logDataModification('bed_hold', residentId, 'RELEASE', prev, data.bedHold, userId);
    return { success: true };
  }

  // ── Evacuation Priority ───────────────────────────────────────────────

  updateEvacuationPriority(residentId, priority, userId = 'system') {
    const data = this._getResidentLeave(residentId);
    if (!data) return { success: false, error: 'Resident leave data not initialised' };

    const validPriorities = ['Priority 1', 'Priority 2', 'Priority 3', 'Priority 4'];
    if (!validPriorities.includes(priority)) {
      return { success: false, error: `Invalid priority. Must be one of: ${validPriorities.join(', ')}` };
    }

    const prev = data.evacuationPriority;
    data.evacuationPriority = priority;
    this._persist(residentId, data);
    logDataModification('evacuation_priority', residentId, 'UPDATE', prev, priority, userId);
    return { success: true, data: priority };
  }

  // ── Statistics ─────────────────────────────────────────────────────────

  getStatistics(residentId) {
    const data = this._getResidentLeave(residentId);
    if (!data) return null;

    const now = new Date();
    const thisYear = now.getFullYear();
    const leaveDays = data.leaveHistory.reduce((total, l) => {
      if (!l.startDate) return total;
      const start = new Date(l.startDate);
      const end = l.endDate ? new Date(l.endDate) : now;
      if (start.getFullYear() === thisYear || end.getFullYear() === thisYear) {
        total += Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
      }
      return total;
    }, 0);

    const socialLeaves = data.leaveHistory.filter((l) => l.type === 'Social Leave').length;
    const hospitalLeaves = data.leaveHistory.filter((l) =>
      l.type === 'Hospital Leave' || l.type === 'Hospital Transfer'
    ).length;
    const activeTransfers = data.hospitalTransfers.filter((t) => t.status === 'In Hospital').length;

    return {
      totalLeaveRecords: data.leaveHistory.length,
      totalHospitalTransfers: data.hospitalTransfers.length,
      leaveDaysThisYear: leaveDays,
      socialLeaves,
      hospitalLeaves,
      activeTransfers,
      bedHoldActive: data.bedHold?.active || false,
      currentStatus: data.currentLeaveStatus,
    };
  }

  // ── Validation ─────────────────────────────────────────────────────────

  _validateLeave(leaveData, existingData) {
    const errors = [];
    if (!leaveData.type) errors.push('Leave type is required');
    if (!leaveData.startDate) errors.push('Start date is required');
    if (!['Social Leave', 'Hospital Leave', 'Respite Leave', 'Transition Leave'].includes(leaveData.type)) {
      errors.push('Invalid leave type');
    }
    if (existingData.currentLeaveStatus !== 'In Facility') {
      errors.push(`Cannot record new leave while resident is ${existingData.currentLeaveStatus}`);
    }
    if (leaveData.endDate && leaveData.startDate && new Date(leaveData.endDate) < new Date(leaveData.startDate)) {
      errors.push('End date cannot be before start date');
    }
    return errors;
  }

  _validateTransfer(transferData) {
    const errors = [];
    if (!transferData.dateOut) errors.push('Transfer date is required');
    if (!transferData.hospital) errors.push('Hospital name is required');
    if (!transferData.reason) errors.push('Reason for transfer is required');
    return errors;
  }
}

export default new LeaveMovementService();
